export type ResourceIcon =
  | "article"
  | "plugs"
  | "lifebuoy"
  | "clock"
  | "building"
  | "chat";

export interface ResourceLink {
  title: string;
  description: string;
  href: string;
  icon: ResourceIcon;
  group: "Learn" | "Product" | "Company";
  planned?: boolean;
}

export const resourceLinks: readonly ResourceLink[] = [
  {
    title: "Blog",
    description: "Research notes, creative teardowns, and practical playbooks.",
    href: "/blog",
    icon: "article",
    group: "Learn",
  },
  {
    title: "Get help",
    description: "Guides for setting up Brand Context and running your first project.",
    href: "/help",
    icon: "lifebuoy",
    group: "Learn",
  },
  {
    title: "Integrations",
    description: "See how Zesrin will connect evidence, assets, and workflows.",
    href: "/integrations",
    planned: true,
    icon: "plugs",
    group: "Product",
  },
  {
    title: "Release notes",
    description: "A clear record of product updates and improvements.",
    href: "/releases",
    planned: true,
    icon: "clock",
    group: "Product",
  },
  {
    title: "About us",
    description: "Why we are building a better creative decision system.",
    href: "/about",
    icon: "building",
    group: "Company",
  },
  {
    title: "Contact us",
    description: "Talk with the team about access, partnerships, or support.",
    href: "/contact",
    icon: "chat",
    group: "Company",
  },
] as const;

export const resourceGroups = ["Learn", "Product", "Company"] as const;

export function isPlannedResource(href: string) {
  return resourceLinks.some((item) => item.href === href && item.planned);
}

export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  category: "Creative research" | "Video analysis" | "Production systems" | "Creator strategy";
  readTime: string;
  date: string;
  accent: "blue" | "pink" | "lime" | "ink";
  featured?: boolean;
}

export const blogPosts: readonly BlogPost[] = [
  {
    slug: "evidence-before-ideas",
    title: "Start with evidence, not another blank brainstorm",
    excerpt:
      "A practical system for turning market movement and proven videos into original creative directions.",
    category: "Creative research",
    readTime: "7 min read",
    date: "August 28, 2026",
    accent: "blue",
    featured: true,
  },
  {
    slug: "decode-a-winning-hook",
    title: "How to decode a winning hook without copying it",
    excerpt:
      "Separate the reusable creative mechanic from the execution details that belong to another brand.",
    category: "Video analysis",
    readTime: "6 min read",
    date: "August 19, 2026",
    accent: "pink",
  },
  {
    slug: "brand-context",
    title: "Brand Context is more than a style guide",
    excerpt:
      "The facts, constraints, audience language, and prior decisions every creative agent needs to share.",
    category: "Production systems",
    readTime: "8 min read",
    date: "August 7, 2026",
    accent: "lime",
  },
  {
    slug: "creator-fit",
    title: "Creator fit starts with the idea, not follower count",
    excerpt:
      "A better shortlist connects audience, delivery style, market, and the specific creative concept.",
    category: "Creator strategy",
    readTime: "5 min read",
    date: "July 24, 2026",
    accent: "ink",
  },
  {
    slug: "traceable-script",
    title: "What makes a script traceable?",
    excerpt:
      "Keep every hook, scene, and proof point connected to the evidence and brand fact that justified it.",
    category: "Production systems",
    readTime: "6 min read",
    date: "July 11, 2026",
    accent: "blue",
  },
  {
    slug: "comments-as-research",
    title: "The comment section is part of the creative",
    excerpt:
      "Use audience responses to distinguish curiosity, confusion, objection, and real purchase intent.",
    category: "Video analysis",
    readTime: "4 min read",
    date: "June 30, 2026",
    accent: "pink",
  },
] as const;

export const blogCategories = [
  "All",
  "Creative research",
  "Video analysis",
  "Production systems",
  "Creator strategy",
] as const;

export type BlogCategory = (typeof blogCategories)[number];
